import { memo, PropsWithChildren, useMemo, useState, MouseEvent, useRef } from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider, Box, Typography } from '@mui/material';

import evaluation from '../../static/EVALUATION.svg';
import flash_flood from '../../static/FLASH FLOOD.svg';
import floodsummmary from '../../static/FLOOD SUMMARY.svg';
import gfm from '../../static/GFM.svg';
import hydrological from '../../static/HYDROLOGICAL.svg';
import init_conditions from '../../static/INIT. CONDITIONS.svg';
import meteorological from '../../static/METEREOLOGICAL.png';
import static_img from '../../static/STATIC.svg';
import { MapActiveLayer, WMSLayer, WMSInfo } from '../../Map.types';
import { LayerToggle } from '../LayerToggle';
import IS_EFAS from '../../../../components/sitebase/Environment';
import { useOutletContext } from 'react-router-dom';
import { OutletIProps } from '../../../../components/sitebase/Base.types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { faStar as frStar } from '@fortawesome/free-regular-svg-icons';
import { WMSListPresentation } from './WMSList.types';

import moment from 'moment';


interface LayerGroupI {
    group: string,
    baseWms: WMSInfo,
    layers: WMSLayer[],
    activeLayers: MapActiveLayer[],
    favourites?: Set<string>,
    setFavouriteLayer?: (layerId: string) => void,
    presentation: WMSListPresentation,
    anonymousUser: boolean,
    forecastEmbargoActive: boolean,
    forecastDate: string,
    disableMouseEvents?: boolean,
    addLayer: (source: string, group: string, layer: WMSLayer) => void
}

export default memo(function LayerGroup(props: PropsWithChildren<LayerGroupI>) {
    const context = useOutletContext<OutletIProps>();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const touchRef = useRef(false);
    const [backdropOpen, setBackdropOpen] = useState(false);

    const handleMouseEnter = (event: MouseEvent<HTMLElement>) => {
        if (touchRef.current) {
            touchRef.current = false;
            return;
        }
        setBackdropOpen(false);
        if (closeTimeout.current) {
            clearTimeout(closeTimeout.current);
            closeTimeout.current = null;
        }
        setAnchorEl(event.currentTarget);
    };

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        touchRef.current = false;
        if (closeTimeout.current) {
            clearTimeout(closeTimeout.current);
            closeTimeout.current = null;
        }
        if (anchorEl) {
            setAnchorEl(null);
        } else {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        closeTimeout.current = setTimeout(() => {
            setAnchorEl(null);
        }, 0);
    };

    const handleMenuEnter = () => {
        if (closeTimeout.current) {
            clearTimeout(closeTimeout.current);
            closeTimeout.current = null;
        }
    };

    let icon = '', id = '';
    if (props.group.toLowerCase().includes('flood summary')) {
        icon = floodsummmary;
        id = 'flood-summary';
    }
    else if (props.group.toLowerCase().includes('hydrological')) {
        icon = hydrological;
        id = 'hydrological';
    }
    else if (props.group.toLowerCase().includes('init. conditions') || props.group.toLowerCase().includes('initial conditions')) {
        icon = init_conditions;
        id = 'init-conditions';
    }
    else if (props.group.toLowerCase().includes('flash flood') || props.group.toLowerCase().includes('flood risk')) {
        icon = flash_flood;
        id = 'flash-flood';
    }
    else if (props.group.toLowerCase().includes('static')) {
        icon = static_img;
        id = 'static';
    }
    else if (props.group.toLowerCase().includes('evaluation')) {
        icon = evaluation;
        id = 'evaluation';
    }
    else if (props.group.toLowerCase().includes('gfm') || props.group.toLowerCase().includes('monitoring')) {
        icon = gfm;
        id = 'gfm';
    }
    else if (props.group.toLowerCase().includes('meteorological')) {
        icon = meteorological;
        id = 'meteorological';
    } else if (props.group.toLowerCase().includes('favourites')) {
        id = 'favourites';
        icon = '';
    }

    const sortedLayers = useMemo(() => {
        return props.layers.sort((a, b) => {
            // primary: keyword order (layers without order go last)
            const aOrder = a.Keywords?.order ? parseInt(a.Keywords.order) : Infinity;
            const bOrder = b.Keywords?.order ? parseInt(b.Keywords.order) : Infinity;
            if (aOrder !== bOrder) return aOrder - bOrder;
            // fallback: entries that start with a number as the first character and then alphabetically
            const a_num = parseInt(a.Name);
            const b_num = parseInt(b.Name);
            if (!isNaN(a_num) && !isNaN(b_num)) {
                return a_num - b_num || a.Title.localeCompare(b.Title);
            } else if (!isNaN(a_num)) {
                return -1;
            } else if (!isNaN(b_num)) {
                return 1;
            } else {
                return a.Title.localeCompare(b.Title);
            }
        });
    }, [props.layers]);

    return (
        <>
            <li key={props.group} id={id} className='list-group-item efas-wms-layer-menu'>
                <Box
                    onClick={handleClick}
                    onMouseEnter={props.disableMouseEvents ? undefined : handleMouseEnter}
                    onMouseLeave={props.disableMouseEvents ? undefined : handleClose}
                    data-testid={`${id}-trigger`}
                    sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '2.5rem',
                        padding: '0.5rem',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        }
                    }}
                    aria-haspopup="true"
                    aria-expanded={open}
                >
                    {props.presentation === WMSListPresentation.ICONS && (
                        <>
                            {id === 'favourites' && <FontAwesomeIcon icon={faStar} style={{ fontSize: '1.5rem' }} />}
                            {icon && (
                                <Box
                                    component="img"
                                    src={icon}
                                    alt={props.group}
                                    loading="lazy"
                                    sx={{
                                        height: '25px',
                                        width: 'auto',
                                        objectFit: 'contain'
                                    }}
                                />
                            )}
                        </>
                    )}
                    {props.presentation === WMSListPresentation.TEXT && (
                        <Typography variant="body1">{props.group}</Typography>
                    )}
                </Box>

                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={() => setAnchorEl(null)}
                    transitionDuration={0}
                    disableAutoFocusItem={true}
                    disableScrollLock={true}
                    disableRestoreFocus={true}
                    hideBackdrop={!backdropOpen}
                    sx={{ pointerEvents: backdropOpen ? 'auto' : 'none' }}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    slotProps={{
                        paper: {
                            onMouseEnter: props.disableMouseEvents ? undefined : handleMenuEnter,
                            onMouseLeave: props.disableMouseEvents ? undefined : handleClose,
                            sx: {
                                maxHeight: '75vh',
                                minWidth: '250px',
                                pointerEvents: 'auto'
                            }
                        }
                    }}
                >
                    <Box sx={{ px: 2, py: 1 }}>
                        <Typography variant="subtitle1" align="center" sx={{ fontWeight: 500 }} className={`${id} pe-none user-select-none`}>
                            {props.group}
                        </Typography>
                    </Box>
                    <Divider />

                    {sortedLayers.map((layer) => {
                        // layers marker as "internal_layer" are not shown in the list of available layers
                        // these layers should not be queried directly by the client, for example
                        // glofas' reportingPoints is an alias for different internal layers depending on the request date
                        if (layer.Keywords) {
                            if (layer.Keywords.internal_layer === "true") {
                                console.debug("Skipping addition of internal layer to layers list", layer.Name, layer);
                                return null;
                            }
                            if (layer.Keywords.forecaster_layer === "true" && context.user.forecaster === false) {
                                console.debug("Skipping addition of forecaster layer to layers list", layer.Name, layer);
                                return null;
                            }
                            if (layer.Keywords.visible_group && !layer.Keywords.visible_group.split('|').some((g) => context.user.groups.includes(g))) {
                                console.debug("Skipping layer not in user's visible groups", layer.Name, layer.Keywords.visible_group);
                                return null;
                            }
                            if (layer.Keywords.show_until && !moment.utc(layer.Keywords.show_until).isAfter(moment.utc(props.forecastDate))) {
                                console.debug("Skipping layer past show_until date", layer.Name, layer.Keywords.show_until);
                                return null;
                            }
                            if (layer.Keywords.show_from && moment.utc(layer.Keywords.show_from).isAfter(moment.utc(props.forecastDate))) {
                                console.debug("Skipping layer before show_from date", layer.Name, layer.Keywords.show_from);
                                return null;
                            }
                        }

                        const toggled = props.activeLayers.find((l) => l.name === layer.Name && l.source === props.baseWms?.url) ? true : false;
                        const layer_allowed = !props.anonymousUser || !props.forecastEmbargoActive || ((IS_EFAS && layer.Keywords && layer.Keywords["public"] === "true" ? true : false));
                        console.debug("Layer allowed:", layer_allowed, "Layer:", layer.Name);
                        const toggleAction = !layer_allowed ?
                            () => { } :
                            () => {
                                props.addLayer(props.baseWms!.url, props.group, layer);
                            };

                        return (
                            <MenuItem
                                key={layer.Name + toggled}
                                data-testid={`wms-layer-${layer.Name.replace(/[^a-zA-Z0-9_-]/g, '-')}`}
                                data-layer-name={layer.Name}
                                data-layer-source={props.baseWms?.name}
                                onClick={toggleAction}
                                disabled={!layer_allowed}
                                sx={{
                                    py: 1,
                                    px: 2,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
                                    <LayerToggle toggled={toggled} id={layer.Name} disabled={!layer_allowed} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={layer.Title}
                                    slotProps={{
                                        primary: {
                                            variant: 'body1',
                                            sx: { flex: 1 }
                                        }
                                    }}
                                />
                                {props.favourites && props.setFavouriteLayer && (
                                    <Box
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (props.setFavouriteLayer) props.setFavouriteLayer(layer.Title);
                                        }}
                                        sx={{
                                            ml: 'auto',
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            className={props.favourites.has(layer.Title) ? 'text-warning' : ''}
                                            icon={props.favourites.has(layer.Title) ? faStar : frStar}
                                        />
                                    </Box>
                                )}
                            </MenuItem>
                        );
                    })}
                </Menu>
            </li>
        </>
    );
});